import { lazy, type ComponentType } from "react";

const refreshKey = "blitzsynth:module-refresh";
const refreshWindow = 60_000;

export function isModuleLoadError(error: unknown): boolean {
  return error instanceof Error && /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk .+ failed|^Unable to preload CSS for \S+/i.test(error.message);
}

/** A failed optional workspace must not tear down the shell or discard its state. */
export function lazyWithRecovery<T extends ComponentType<any>>(load: () => Promise<{ default: T }>) {
  return lazy(async () => {
    try { return await load(); }
    catch (error) {
      if (!isModuleLoadError(error)) throw error;
      return { default: ModuleRecovery as unknown as T };
    }
  });
}

export function canRefreshModule(storage: Pick<Storage, "getItem">, now: number): boolean {
  try {
    const previous = Number(storage.getItem(refreshKey));
    return !previous || now - previous > refreshWindow;
  } catch { return true; }
}

export function ModuleRecovery() {
  const allowed = canRefreshModule(window.sessionStorage, Date.now());
  function refresh() {
    if (!canRefreshModule(window.sessionStorage, Date.now())) return;
    // Deliberately user initiated: target drawings and unsaved form input may
    // exist in memory. Never refresh automatically while a chemist is working.
    try { window.sessionStorage.setItem(refreshKey, String(Date.now())); } catch { /* storage can be unavailable */ }
    window.location.reload();
  }
  return <section role="alert" className="workspace-page module-recovery">
    <h2>This view couldn’t load</h2>
    <p>A new version may have been published, or your connection was interrupted. Your current session has not been refreshed.</p>
    <p>Copy any unsaved input before refreshing the app. Saved searches remain available in History.</p>
    {allowed ? <button type="button" className="primary-button" onClick={refresh}>Refresh app</button> : <p>Refreshing didn’t resolve this. Check your connection and try again in a minute.</p>}
  </section>;
}
